import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Volume2, Square } from "lucide-react";
import { playVoice, stopVoice } from "@/lib/ariel";
import PlayAllButton from "@/components/app/PlayAllButton";

export const VoiceButton = ({ text, testId }) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    setOn(false);
    return () => stopVoice();
  }, [text]);
  const toggle = async () => {
    if (on) {
      stopVoice();
      setOn(false);
      return;
    }
    setOn(true);
    await playVoice(text).catch(() => {});
    setOn(false);
  };
  return (
    <button data-testid={testId} onClick={toggle} className="inline-flex items-center gap-1.5 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A] hover:text-[#F2E8DF] transition-colors">
      {on ? <><Square className="w-3 h-3" /> Stop</> : <><Volume2 className="w-3.5 h-3.5" /> Hear Ariel</>}
    </button>
  );
};

// Swipeable teaching cards, one key insight at a time.
export default function TeachingCards({ cards, onDone, doneLabel }) {
  const [i, setI] = useState(0);
  const card = cards[i];
  const go = (d) => setI((k) => Math.min(cards.length - 1, Math.max(0, k + d)));
  const last = i === cards.length - 1;
  return (
    <div data-testid="qi-cards">
      <div className="mb-4"><PlayAllButton texts={cards.map((c) => `${c.title}. ${c.text}`)} onIndex={setI} testId="qi-play-all" /></div>
      <div className="flex gap-1.5" aria-hidden>
        {cards.map((_, k) => <span key={k} className={`h-[3px] flex-1 rounded-full transition-colors duration-500 ${k <= i ? "bg-[#D9B47A]" : "bg-[#D9B47A]/15"}`} />)}
      </div>
      <motion.article
        key={i}
        data-testid={`qi-card-${i}`}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.35}
        onDragEnd={(_, info) => { if (info.offset.x < -70) go(1); else if (info.offset.x > 70) go(-1); }}
        initial={{ opacity: 0.3, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="mt-6 min-h-[340px] flex flex-col border border-[#E8838E]/25 bg-gradient-to-b from-[#1A0B12] to-[#0F070B] p-7 touch-pan-y cursor-grab active:cursor-grabbing select-none"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#A8998C]">Teaching {String(i + 1).padStart(2, "0")} / {String(cards.length).padStart(2, "0")}</span>
          <VoiceButton text={`${card.title}. ${card.text}`} testId="qi-card-voice" />
        </div>
        <h3 className="mt-6 font-display text-3xl leading-tight text-[#F2E8DF]">{card.title}</h3>
        <p className="mt-5 text-[17px] leading-[1.75] text-[#DCCFC3]">{card.text}</p>
      </motion.article>
      <p className="mt-3 text-center text-[12px] text-[#A8998C]">Swipe or use the arrows</p>
      <div className="mt-5 flex items-center gap-3">
        <button data-testid="qi-card-prev" onClick={() => go(-1)} disabled={i === 0} aria-label="Previous" className="w-12 h-12 rounded-full border border-[#D9B47A]/30 flex items-center justify-center text-[#D9B47A] disabled:opacity-30 hover:border-[#D9B47A] transition-colors"><ArrowLeft className="w-4 h-4" /></button>
        {last ? (
          <button data-testid="qi-cards-done" onClick={onDone} className="flex-1 rounded-full bg-[#D9B47A] text-[#0A0706] py-3.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors">{doneLabel}</button>
        ) : (
          <button data-testid="qi-card-next" onClick={() => go(1)} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#D9B47A]/40 py-3.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">Next <ArrowRight className="w-4 h-4" /></button>
        )}
      </div>
    </div>
  );
}
